import React, { useEffect, useState, useCallback } from "react";
import "./EditCohortModel.scss";
import { Modal } from "react-bootstrap";
import exportimages from "../../../../assets/images/exportImages";
import { useDispatch } from "react-redux";
import {
  editCohortSlice,
  setCohortApplied,
  setMode,
  setSelectedCohort,
} from "../../../../rtk-query/features/cohort/cohortSlice";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSubscription } from "../../../../hooks/useSubscription";
import { PRIVILEGES } from "../../../../utils/privileges";
import { COHORT_MODE } from "../CohortList/CohortList";

/* -------------------- CONSTANTS -------------------- */
export const EDIT_MODEL = {
  EDIT: "edit",
  SUCCESS: "success",
};

/* -------------------- HELPERS -------------------- */
const containsEmoji = (value = "") =>
  /[\p{Extended_Pictographic}]/u.test(value);

const validateField = (name, value) => {
  if (name === "cohortTitle") {
    if (!value) return "Cohort name is required";
    if (containsEmoji(value)) return "Emojis are not allowed";
  }

  if (name === "cohortDesc" && containsEmoji(value)) {
    return "Emojis are not allowed";
  }

  return "";
};

/* -------------------- FORM COMPONENT -------------------- */
const EditCohortForm = ({
  form,
  errors,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting,
  isAllowed,
}) => (
  <div className="edit-cohort-modal">
    <div className="modal-header">
      <div className="icon-outer-circle">
        <div className="icon-inner-circle">
          <img alt="icon" src={exportimages.edit_cohort_icon} />
        </div>
      </div>
    </div>

    <h2 className="mb-10">Edit Cohort Details</h2>

    <div className="form-group">
      <label>Cohort Name</label>
      <input
        type="text"
        name="cohortTitle"
        value={form.cohortTitle}
        placeholder="Enter cohort name"
        onChange={onChange}
      />
      {errors.cohortTitle && (
        <span className="error-text">{errors.cohortTitle}</span>
      )}
    </div>

    <div className="form-group">
      <label>Description</label>
      <textarea
        name="cohortDesc"
        value={form.cohortDesc}
        placeholder="Enter cohort description"
        onChange={onChange}
      />
      {errors.cohortDesc && (
        <span className="error-text">{errors.cohortDesc}</span>
      )}
    </div>

    <div className="actions">
      <button className="btn cancel" onClick={onCancel}>
        Cancel
      </button>

      <button
        className="btn primary"
        onClick={onSubmit}
        disabled={isAllowed ? (isSubmitting ? true : false) : true}
      >
        {isSubmitting ? "Saving..." : "Save Edits"}
      </button>
    </div>
  </div>
);

/* -------------------- SUCCESS COMPONENT -------------------- */
const EditCohortSuccess = ({ title, onClose }) => (
  <div className="success-cohort-modal">
    <div className="modal-header">
      <div className="icon-outer-circle">
        <div className="icon-inner-circle">
          <img alt="icon" src={exportimages.cohort_success_icon} />
        </div>
      </div>
    </div>

    <h2>Cohort Saved Successfully</h2>
    <p className="subtitle">
      Cohort <strong>{title}</strong> was saved successfully.
    </p>

    <div className="actions">
      <button className="btn cancel" onClick={onClose}>
        Close
      </button>
    </div>
  </div>
);

/* -------------------- MAIN COMPONENT -------------------- */
export const EditCohortModel = (props) => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const { hasSubscription } = useSubscription();

  const [form, setForm] = useState({
    cohortTitle: "",
    cohortDesc: "",
  });

  const [errors, setErrors] = useState({
    cohortTitle: "",
    cohortDesc: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  /* -------------------- INIT FROM PROPS -------------------- */
  useEffect(() => {
    if (props.switchEditModel === EDIT_MODEL.EDIT) {
      setForm({
        cohortTitle: props.title || "",
        cohortDesc: props.subtitle || "",
      });

      setErrors({
        cohortTitle: "",
        cohortDesc: "",
      });
    }
  }, [props.switchEditModel, props.title, props.subtitle]);

  /* -------------------- HANDLERS -------------------- */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    const errorMsg = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  }, []);

  const isFormValid = form.cohortTitle && !errors.cohortTitle;

  const handleEdit = async () => {
    if (!isFormValid) return;

    try {
      setIsSubmitting(true);

      const data = await dispatch(
        editCohortSlice({
          ...form,
          uid: props.uid,
        }),
      ).unwrap();

      props.setSwitchEditModel(EDIT_MODEL.SUCCESS);

      dispatch(setMode(COHORT_MODE.CUSTOM));
      dispatch(setCohortApplied(null));
      dispatch(setSelectedCohort(data?.data));
    } catch (err) {
      console.error("Edit cohort failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAllowed =
    hasPermission(PRIVILEGES.CREATE_COHORT) &&
    hasSubscription(PRIVILEGES.CREATE_COHORT) &&
    isFormValid;

  /* -------------------- UI -------------------- */
  return (
    <Modal {...props} centered>
      <Modal.Body className="cohort-modal">
        {props.switchEditModel === EDIT_MODEL.EDIT ? (
          <EditCohortForm
            form={form}
            errors={errors}
            onChange={handleChange}
            onSubmit={handleEdit}
            onCancel={props.onHide}
            isSubmitting={isSubmitting}
            isAllowed={isAllowed}
          />
        ) : (
          <EditCohortSuccess title={form.cohortTitle} onClose={props.onHide} />
        )}
      </Modal.Body>
    </Modal>
  );
};
